import { isServer } from "core-lib/utils/utils";
import dayjs from "dayjs";
import "pikaday/css/pikaday.css";
import React, { useCallback, useEffect, useRef, useState } from "react";
import s from "./Calendar.module.scss";

interface CalendarProps {
  selectedDate?: string;
  onSelect?: (date: string) => void;
  disablePreviousDates?: boolean;
  disableFutureDates?: boolean;
  dateFormat?: string;
  disableWeekends?: boolean;
  disabledDays?: number[];
  modifiers?: Record<string, Date[]>;
  modifiersStyles?: Record<string, React.CSSProperties>;
  disableMonthYearChange?: boolean;
  onMonthChange?: (date: string) => void;
  format?: string;
  minDate?: string;
  maxDate?: string;
}

let Pikaday: any = null;

export const Calendar: React.FC<CalendarProps> = ({
  selectedDate,
  onSelect,
  disablePreviousDates,
  disableFutureDates,
  dateFormat = "YYYY-MM-DD",
  disableWeekends = false,
  disabledDays,
  modifiers,
  modifiersStyles,
  disableMonthYearChange = false,
  onMonthChange,
  format,
  minDate,
  maxDate,
}) => {
  const modifiersRef = useRef(null);
  const [ready, setReady] = useState(false);
  const pickerRef = useRef<HTMLDivElement | null>(null);
  const pickerInstance = useRef<any>(null);
  const lastMonth = useRef<number | null>(null);

  const onSelectCallback = useCallback(
    (date: Date) => {
      onSelect?.(dayjs(date).format(dateFormat));
    },
    [onSelect, dateFormat]
  );

  const disableDayFn = useCallback(
    (date: Date) => {
      const now = new Date();
      if (disablePreviousDates && date < now) return true;
      if (disableFutureDates && date > now) return true;
      if (disabledDays?.includes(date.getDay())) return true;
      return false;
    },
    [disablePreviousDates, disableFutureDates, disabledDays]
  );

  const drawModifiers = (mod) => {
    const tdList = pickerInstance.current?.el?.querySelectorAll("td");
    if (!tdList) return;
    tdList.forEach((td) => {
      const button = td.querySelector("button.pika-day");
      if (button) {
        const dayText = button.textContent;

        const calendar = pickerInstance.current.calendars?.[0];
        if (!calendar) return;

        const calendarMonth = calendar.month;
        const calendarYear = calendar.year;

        const cellDate = new Date(
          calendarYear,
          calendarMonth,
          parseInt(dayText || "0", 10)
        );

        Object.entries(mod || {}).forEach(([modifierName, dates]) => {
          //@ts-ignore
          dates.forEach((modDate) => {
            if (
              cellDate.getFullYear() === modDate.getFullYear() &&
              cellDate.getMonth() === modDate.getMonth() &&
              cellDate.getDate() === modDate.getDate()
            ) {
              const styles = modifiersStyles?.[modifierName];
              if (styles) {
                Object.entries(styles).forEach(([key, value]) => {
                  (button.style as any)[key] = value;
                });
              }
            }
          });
        });
      }
    });
  };

  const onDraw = useCallback(() => {
    requestAnimationFrame(() => {
      if (!pickerInstance.current) return;
      const calendar = pickerInstance.current.calendars?.[0];
      if (!calendar) return;
      const { month, year } = calendar;
      if (month !== lastMonth.current) {
        lastMonth.current = month;
        onMonthChange?.(dayjs(new Date(year, month, 1)).format(dateFormat));
      }
      drawModifiers(modifiersRef.current);
    });
  }, [modifiersRef.current]);

  useEffect(() => {
    if (!isServer() && pickerRef.current) {
      import("pikaday").then((module) => {
        Pikaday = module.default;
        setReady(true);
      });
    }
    return () => {
      if (pickerInstance.current) {
        pickerInstance.current.destroy();
        pickerInstance.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (ready && pickerRef.current) {
      if (pickerInstance.current) pickerInstance.current.destroy();
      const instance = new Pikaday({
        field: pickerRef.current,
        bound: false,
        defaultDate: selectedDate ? new Date(selectedDate) : new Date(),
        setDefaultDate: !!selectedDate,
        yearRange: [1900, 2100],
        disableWeekends,
        disableDayFn,
        onSelect: onSelectCallback,
        onDraw,
        minDate: minDate && dayjs(minDate).toDate(),
        maxDate: maxDate && dayjs(maxDate).toDate(),
        format,
      });

      if (disableMonthYearChange) {
        const styleTagId = "disable-pika-style";
        if (!document.getElementById(styleTagId)) {
          const style = document.createElement("style");
          style.id = styleTagId;
          style.innerHTML = `
            .pika-select { display: none !important; }
            .pika-label:after { display: none !important; }
          `;
          document.head.appendChild(style);
        }
      }

      instance.show();
      pickerInstance.current = instance;
    }
  }, [ready, minDate, maxDate, format]);

  useEffect(() => {
    if (pickerInstance.current && selectedDate) {
      const current = pickerInstance.current.getDate();
      const next = new Date(selectedDate);
      if (
        current.getFullYear() !== next.getFullYear() ||
        current.getMonth() !== next.getMonth() ||
        current.getDate() !== next.getDate()
      ) {
        pickerInstance.current.setDate(next, true);
      }
    }
  }, [selectedDate]);

  useEffect(() => {
    modifiersRef.current = modifiers;
    if (pickerInstance.current) {
      pickerInstance.current.draw();
    }
  }, [JSON.stringify(modifiers)]);

  return (
    <div className={s.root}>
      <div id="pickerRef" ref={pickerRef}></div>
    </div>
  );
};
